import React, { useState, useEffect } from 'react'

import * as StringFormatter from '../../../../global/formatters/string-formatter.js'
import { getRelativeBasePath } from '../../../../global/environments/path.js'
import { getClosedEndFundsData } from '../InvestmentProductsDataSource.js'
import Table from '../../../../../src/components/Table/Table.jsx'
import { stickyOnScroll } from '../helpers/InvestmentProductsHelper.js'
import '../InvestmentProducts.css'

export default function ClosedEndFundsContainer() {
  const [fundRoot, setFundRoot] = useState({})
  const [funds, setFunds] = useState([])
  const [selectedPerformanceFrequency, setSelectedPerformanceFrequency] =
    useState('Monthly')
  const [tableHeaderTop, setTableHeaderTop] = useState()

  const getAspect = (section) => {
    const aspects = [
      {
        name: '#closed-end-funds',
        info: {
          tag: selectedPerformanceFrequency,
          selector: [
            {
              name: 'value',
              isArray: false,
            },
            {
              name: 'closedEndFunds',
              isArray: false,
            },
          ],
          getData: getClosedEndFundsData,
          renderOptions: () => {
            return (
              <>
                <div className="u-flex u-sm-flexCol u-flexAlignItemsCenter u-sm-flexAlignItemsStart">
                  {renderPerformanceOptions()}
                </div>
              </>
            )
          },
          columnSummaries: [
            {
              name: 'headerSummary0',
              colSpan: 4,
              content: () => '',
            },
            {
              name: 'headerSummary1',
              colSpan: 2,
              content: () => {
                return <>Cumulative Total Returns (%)</>
              },
            },
            {
              name: 'headerSummary2',
              colSpan: 5,
              content: () => {
                return <>Average Annual Total Returns (%)</>
              },
            },
          ],
          columns: [
            {
              name: 'fundName',
              className: 'Table-header',
              ariaLabel: 'Fund Name',
              content: <div>Name</div>,
              cell: {
                content: (fund) => (
                  <a
                    className="u-textBold u-sm-textNone"
                    href={
                      getRelativeBasePath() +
                      `/investment-products/closed-end-funds/${StringFormatter.urlSafe(
                        fund.fundName
                      )}/Class-None/details/?cusip=${fund.cusip}`
                    }
                  >
                    {fund.fundName}
                  </a>
                ),
              },
            },
            {
              name: 'annualExpense',
              className: 'Table-header u-textRight',
              ariaLabel: 'Annual Expenses',
              content: <div className="u-textRight">Annual Expenses</div>,
              cell: {
                className: 'u-textRight',
                content: (fund) => (
                  <div className="u-textNoWrap">{fund.annualExpense}</div>
                ),
              },
            },
            {
              name: 'premiumByDiscount',
              className: 'Table-header u-textRight',
              ariaLabel: 'Premium/Discount',
              content: (
                <div className="u-textRight">
                  Premium/
                  <br />
                  Discount
                </div>
              ),
              cell: {
                className: 'u-textRight',
                content: (fund) => (
                  <div className="u-textNoWrap">{fund.premiumByDiscount}</div>
                ),
              },
            },
            {
              name: 'valueAsOfDate',
              className: 'Table-header u-textRight bordered',
              ariaLabel: 'Price as of',
              content: (
                <>
                  Price as of
                  <br />
                  <div className="u-textNoWrap">
                    {StringFormatter.formatDateStandard(fundRoot.priceAsOfDate)}
                  </div>
                </>
              ),
              cell: {
                className: 'bordered-right u-textRight u-paddingRight0',
                content: (fund) => (
                  <>
                    <div className="u-flex u-flexAlignItemsCenter u-borderColorNeutralLight u-borderBottom u-borderTop0 u-borderLeft0 u-borderRight0 u-paddingRight2gu">
                      <div className="u-paddingVert2gu u-paddingRight8gu">
                        NAV
                      </div>
                      <div className="u-paddingVert2gu">{fund.nav}</div>
                    </div>
                    <div className="u-flex u-flexAlignItemsCenter u-paddingRight2gu">
                      <div className="u-paddingVert2gu u-paddingRight8gu">
                        MKT
                      </div>
                      <div className="u-paddingVert2gu">{fund.mktValue}</div>
                    </div>
                  </>
                ),
              },
            },
            {
              name: 'valueOneMonth',
              className: 'Table-header u-textRight',
              ariaLabel: 'Cumulative, 1 month',
              content: <>1 Mo</>,
              cell: {
                className: 'u-textRight u-paddingLeft0 u-paddingRight0',
                content: (fund) => (
                  <>
                    <div className="u-padding2gu u-borderColorNeutralLight u-borderBottom u-borderTop0 u-borderLeft0 u-borderRight0">
                      {fund.navOneMonth}
                    </div>
                    <div className="u-padding2gu">{fund.mktValueOneMonth}</div>
                  </>
                ),
              },
            },
            {
              name: 'valueThreeMonth',
              className: 'Table-header u-textRight bordered',
              ariaLabel: 'Cumulative, 3 month',
              content: <>3 Mo</>,
              cell: {
                className:
                  'u-textRight bordered-right u-paddingLeft0 u-paddingRight0',
                content: (fund) => (
                  <>
                    <div className="u-padding2gu u-borderColorNeutralLight u-borderBottom u-borderTop0 u-borderLeft0 u-borderRight0">
                      {fund.navThreeMonth}
                    </div>
                    <div className="u-padding2gu">
                      {fund.mktValueThreeMonth}
                    </div>
                  </>
                ),
              },
            },
            {
              name: 'valueOneYear',
              className: 'Table-header u-textRight',
              ariaLabel: '1 Year',
              content: <>1 Yr</>,
              cell: {
                className: 'u-textRight u-paddingLeft0 u-paddingRight0',
                content: (fund) => (
                  <>
                    <div className="u-padding2gu u-borderColorNeutralLight u-borderBottom u-borderTop0 u-borderLeft0 u-borderRight0">
                      {fund.navOneYear}
                    </div>
                    <div className="u-padding2gu">{fund.mktValueOneYear}</div>
                  </>
                ),
              },
            },
            {
              name: 'valueThreeYear',
              className: 'Table-header u-textRight',
              ariaLabel: '3 Year',
              content: <>3 Yr</>,
              cell: {
                className: 'u-textRight u-paddingLeft0 u-paddingRight0',
                content: (fund) => (
                  <>
                    <div className="u-padding2gu u-borderColorNeutralLight u-borderBottom u-borderTop0 u-borderLeft0 u-borderRight0">
                      {fund.navThreeYear}
                    </div>
                    <div className="u-padding2gu">{fund.mktValueThreeYear}</div>
                  </>
                ),
              },
            },
            {
              name: 'valueFiveYear',
              className: 'Table-header u-textRight',
              ariaLabel: '5 Year',
              content: <>5 Yr</>,
              cell: {
                className: 'u-textRight u-paddingLeft0 u-paddingRight0',
                content: (fund) => (
                  <>
                    <div className="u-padding2gu u-borderColorNeutralLight u-borderBottom u-borderTop0 u-borderLeft0 u-borderRight0">
                      {fund.navFiveYear}
                    </div>
                    <div className="u-padding2gu">{fund.mktValueFiveYear}</div>
                  </>
                ),
              },
            },
            {
              name: 'valueTenYear',
              className: 'Table-header u-textRight',
              ariaLabel: '10 Year',
              content: <>10 Yr</>,
              cell: {
                className: 'u-textRight u-paddingLeft0 u-paddingRight0',
                content: (fund) => (
                  <>
                    <div className="u-padding2gu u-borderColorNeutralLight u-borderBottom u-borderTop0 u-borderLeft0 u-borderRight0">
                      {fund.navTenYear}
                    </div>
                    <div className="u-padding2gu">{fund.mktValueTenYear}</div>
                  </>
                ),
              },
            },
            {
              name: 'valueSinceInception',
              className: 'Table-header u-textNoWrap u-textRight',
              ariaLabel: 'Since Inception',
              content: (
                <>
                  Since
                  <br />
                  Incept. *
                </>
              ),
              cell: {
                className: 'u-textRight u-paddingLeft0 u-paddingRight0',
                content: (fund) => (
                  <>
                    <div className="u-padding2gu u-borderColorNeutralLight u-borderBottom u-borderTop0 u-borderLeft0 u-borderRight0">
                      {fund.navSinceInception}
                    </div>
                    <div className="u-padding2gu">
                      {fund.mktValueSinceInception}
                    </div>
                  </>
                ),
              },
            },
          ],
        },
      },
    ]

    return aspects.find((a) => a.name === section).info
  }

  useEffect(() => {
    initializeFunds()
  }, [selectedPerformanceFrequency])

  useEffect(() => {
    if (funds && funds.length > 0) {
      window.prerenderReady = true
    }
  }, [funds])

  useEffect(() => {
    stickyOnScroll(setTableHeaderTop)
  }, [])

  const downloadFunds = (type) => {
    let url = ''

    url += '/cmg.svc/exportsummary'
    url += '?fundGroupName=ClosedEnd'
    url += '&className='
    url += '&fileType=' + type

    window.location = url
  }

  const initializeFunds = async () => {
    const aspect = getAspect('#closed-end-funds')

    const data = await aspect.getData(aspect.tag)

    const _funds = aspect.selector.reduce(
      (prev, curr) => (curr.isArray ? prev[curr.name][0] : prev[curr.name]),
      data
    )

    setFunds(_funds)

    setFundRoot(_funds[0])
  }

  const onChangePerformanceFrequencyDropdown = (event) => {
    setSelectedPerformanceFrequency(
      event.currentTarget.selectedOptions[0].value
    )
  }

  const onClickDownloadCSV = (event) => {
    event.preventDefault()

    downloadFunds('csv')
  }

  const onClickDownloadTSV = (event) => {
    event.preventDefault()

    downloadFunds('tsv')
  }

  const renderPerformanceOptions = () => {
    return (
      <div className="u-lg-flex u-lg-flexAlignItemsCenter">
        Performance
        <div className="Dropdown u-sm-marginLeft0 u-md-marginLeft0 u-marginLeft2gu u-sm-marginVert1gu">
          <label className="u-hiddenVisually" htmlFor="frequency_dropdown">
            Frequency
          </label>
          <select
            id="frequency_dropdown"
            className="Dropdown-menu u-sm-marginBottom3gu u-md-marginBottom3gu"
            value={selectedPerformanceFrequency}
            onChange={onChangePerformanceFrequencyDropdown}
          >
            <option value="Monthly">MONTHLY</option>
            <option value="Quarterly">QUARTERLY</option>
          </select>
        </div>
        <div className="u-sm-marginLeft0 u-md-marginLeft0 u-marginLeft2gu">
          {funds.length > 0
            ? 'All data as of ' +
              StringFormatter.formatDateStandard(funds[0].asOfDate) +
              ' unless otherwise indicated.'
            : ''}
        </div>
      </div>
    )
  }

  const aspect = getAspect('#closed-end-funds')

  return (
    <div className="investment-products">
      <div className="u-sizeConstrained Content u-marginBottom6gu">
        {/* OPTIONS */}
        <div className="u-flex u-flexAlignItemsCenter u-flexJustifyBetween u-marginVert4gu">
          {aspect.renderOptions()}
        </div>
        <div className="InvestmentProductTable">
          <Table
            columns={aspect.columns}
            columnSummaries={aspect.columnSummaries}
            rows={funds}
            mode={'no-stripe'}
            scrollOffset={tableHeaderTop}
          />
        </div>
      </div>
      <div id="ampf-trace-loaded-page"></div>
    </div>
  )
}
